/**
 * FormFillPanel — data-entry / review surface inside the records workspace.
 *
 * Thin orchestrator around shared form-engine + records-API + UI building
 * blocks. Per-responsibility module split:
 *
 *   form-fill-data.ts          — fetchFormData, getNextInstanceIndex
 *   version-upgrade-sidebar.tsx — resizable version-upgrade panel + RPC call
 *   form-fill-banners.tsx      — stateless banner row (errors, notices)
 *   field-comment-thread.tsx   — per-field comment thread (unchanged)
 *
 * What stays here: state machine (justifications + dirty flag + submission
 * version), the auto-save / submit mutation, and the JSX wiring everything
 * to <FormRenderer mode="data-entry">.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, Send, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { useProjectRole } from '@/features/auth/context/project-role-context'
import { useToast } from '@/hooks/use-toast'
import {
  submitForm,
  fetchFieldComments,
  ConflictError,
  RecordLockedError,
  FormLockedError,
  type CommentRow,
} from '../api/records-api'
import { insertFileRef } from '../api/file-upload-api'
import { FieldCommentThread } from './field-comment-thread'
import { JustificationHistory } from './justification-history'
import type { SoftWarningFlag } from '@/lib/form-engine'
import {
  FormRenderer,
  runFormEngine,
  hydrateJustificationsFromFlags,
  pruneStaleJustifications,
  serializeSoftWarningFlags,
  missingJustifications,
} from '@/lib/form-engine'
import {
  fetchFormData,
  getNextInstanceIndex,
  isUploadedFileRef,
} from './form-fill-data'
import { VersionUpgradeSidebar } from './version-upgrade-sidebar'
import { FormFillBanners } from './form-fill-banners'
import { ResizableSplit } from '@/components/shared/resizable-split'

export interface FormFillPanelProps {
  projectId: string
  recordId: string
  formId: string
  /** for repeatable form instance; if absent + repeatable → new instance */
  submissionId?: string
  onSaved?: (submitted: boolean) => void
}

export function FormFillPanel({
  projectId, recordId, formId, submissionId: submissionIdProp, onSaved,
}: FormFillPanelProps) {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const { canEdit } = useProjectRole()
  const { toast } = useToast()

  const [instanceIndex, setInstanceIndex] = useState<number | null>(submissionIdProp ? 1 : null)
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({})
  const [submissionId, setSubmissionId] = useState<string | null>(submissionIdProp ?? null)
  const [submissionVersion, setSubmissionVersion] = useState(1)
  const [isDirty, setIsDirty] = useState(false)
  const [justifications, setJustifications] = useState<Record<string, string>>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [versionUpgradeChoice, setVersionUpgradeChoice] =
    useState<null | 'pending' | 'continue' | 'upgrade'>(null)
  const [versionSidebarOpen, setVersionSidebarOpen] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null)
  const [tick, setTick] = useState(0)  // re-render to refresh "Xs ago"

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestFieldValuesRef = useRef<Record<string, unknown>>({})
  const latestSubmissionIdRef = useRef<string | null>(submissionIdProp ?? null)
  const saveMutationRef = useRef<{
    mutate: (args: { submit: boolean; submittingValues: Record<string, unknown> }) => void
    isPending: boolean
  } | null>(null)

  useEffect(() => {
    if (submissionIdProp) { setInstanceIndex(1); return }
    getNextInstanceIndex(formId, recordId).then((nextIdx) => {
      setInstanceIndex(nextIdx === 1 ? 1 : nextIdx)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, recordId, submissionIdProp])

  const { data, isLoading, error } = useQuery({
    queryKey: ['form-fill', formId, recordId, instanceIndex, submissionIdProp],
    queryFn: () => fetchFormData(formId, recordId, instanceIndex!, submissionIdProp ?? undefined),
    enabled: !!formId && !!recordId && instanceIndex !== null,
  })

  const { data: fieldComments = [] } = useQuery<CommentRow[]>({
    queryKey: ['field-comments', recordId, submissionId],
    queryFn: () => fetchFieldComments(recordId, submissionId!),
    enabled: !!recordId && !!submissionId,
  })

  useEffect(() => {
    if (!data) return
    if (data.submission) {
      const fv = (data.submission.field_values as Record<string, unknown>) ?? {}
      latestFieldValuesRef.current = fv
      latestSubmissionIdRef.current = data.submission.id as string
      setFieldValues(fv)
      setSubmissionId(data.submission.id as string)
      setSubmissionVersion(data.submission.version as number)
      // C2 — rehydrate persisted justifications so prior clinical rationale
      // shows in the form on resume / revision.
      setJustifications(hydrateJustificationsFromFlags(data.submission.soft_warning_flags))
    }
    if (data.isPinnedOldVersion && versionUpgradeChoice === null) {
      setVersionUpgradeChoice('pending')
      setVersionSidebarOpen(true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // Reset state when the form/record/submission changes (panel reuse)
  useEffect(() => {
    setFieldValues({})
    setJustifications({})
    setSaveError(null)
    setIsDirty(false)
    setSubmitAttempted(false)
    setVersionUpgradeChoice(null)
    setVersionSidebarOpen(false)
    setSubmissionId(submissionIdProp ?? null)
    latestFieldValuesRef.current = {}
    latestSubmissionIdRef.current = submissionIdProp ?? null
    if (submissionIdProp) {
      setInstanceIndex(1)
    } else {
      setInstanceIndex(null)
      getNextInstanceIndex(formId, recordId).then(setInstanceIndex)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, recordId, submissionIdProp])

  const setField = useCallback((key: string, value: unknown) => {
    const next = { ...latestFieldValuesRef.current, [key]: value }
    latestFieldValuesRef.current = next
    setFieldValues(next)
    setIsDirty(true)
    setSaveError(null)

    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = setTimeout(() => {
      autoSaveTimer.current = null
      const sid = latestSubmissionIdRef.current
      const sm = saveMutationRef.current
      if (sid && sm && !sm.isPending) {
        sm.mutate({ submit: false, submittingValues: latestFieldValuesRef.current })
      }
    }, 1500)
  }, [])

  useEffect(() => () => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
  }, [])

  // I1 — re-render every 15s so "saved Xs ago" stays current.
  useEffect(() => {
    if (lastSavedAt === null) return
    const id = setInterval(() => setTick((t) => t + 1), 15_000)
    return () => clearInterval(id)
  }, [lastSavedAt])
  void tick

  const liveWarnings = useMemo(() => {
    if (!data) return []
    return runFormEngine(
      data.definition.fields,
      data.definition.conditional_rules ?? [],
      fieldValues,
    ).softWarnings
  }, [data, fieldValues])

  const missingJustList = useMemo(
    () => missingJustifications(liveWarnings, justifications),
    [liveWarnings, justifications],
  )

  const saveMutation = useMutation({
    mutationFn: async ({
      submit, submittingValues,
    }: { submit: boolean; submittingValues: Record<string, unknown> }) => {
      if (!data || !user || instanceIndex === null) throw new Error('Data not ready')

      // Drop calculated fields — derived at render, never persisted.
      const calculatedKeys = new Set(
        (data.definition.fields ?? [])
          .filter((f) => f.field_type === 'calculated')
          .map((f) => f.field_key),
      )
      const saveValues: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(submittingValues)) {
        if (!calculatedKeys.has(k)) saveValues[k] = v
      }

      // Recompute warnings against save-time values so persisted flags match.
      const liveAtSave = runFormEngine(
        data.definition.fields,
        data.definition.conditional_rules ?? [],
        submittingValues,
      ).softWarnings

      // C1+C3+C4 — prune stale, then serialize live with appended history.
      const previousFlags = data.submission?.soft_warning_flags ?? []
      const pruned = pruneStaleJustifications(previousFlags, liveAtSave)
      const softWarningFlags = serializeSoftWarningFlags({
        liveWarnings: liveAtSave,
        justifications,
        previousFlags: pruned,
        userId: user.id,
      })

      return submitForm({
        submissionId: submissionId ?? null,
        recordId,
        formId,
        formVersionId: data.versionId,
        instanceIndex,
        expectedVersion: submissionVersion,
        userId: user.id,
        projectId,
        fieldValues: saveValues,
        softWarningFlags,
        submit,
      })
    },
    onSuccess: async (
      { submissionId: newSubId, newVersion, recordStatusChanged, recordReopened },
      { submit, submittingValues },
    ) => {
      latestSubmissionIdRef.current = newSubId
      setSubmissionId(newSubId)
      setSubmissionVersion(newVersion)
      setIsDirty(false)
      setSaveError(null)
      setLastSavedAt(Date.now())
      qc.invalidateQueries({ queryKey: ['record-forms', projectId, recordId] })
      qc.invalidateQueries({ queryKey: ['form-instances', recordId, formId] })
      qc.invalidateQueries({ queryKey: ['summary-card', projectId, recordId] })

      // Editing a previously-approved record reopened it — make that visible.
      if (recordReopened) {
        toast({
          title: 'سابقه بازگشایی شد',
          description: 'ویرایش روی سابقه تأییدشده باعث بازگشت آن به حالت «در حال تکمیل» شد.',
        })
      }

      if (data && user) {
        for (const field of data.definition.fields) {
          if (field.field_type !== 'file' && field.field_type !== 'image') continue
          const ref = submittingValues[field.field_key]
          if (isUploadedFileRef(ref)) {
            void insertFileRef(newSubId, field.field_key, ref, user.id)
          }
        }
      }

      if (recordStatusChanged) {
        qc.invalidateQueries({ queryKey: ['record', recordId] })
      }

      if (submit) {
        qc.invalidateQueries({ queryKey: ['form-fill', formId, recordId] })
        toast({ title: 'فرم با موفقیت ارسال شد', description: data?.formTitle })
        onSaved?.(true)
      } else {
        toast({ title: 'ذخیره شد', description: 'پیش‌نویس ذخیره شد', duration: 2000 })
        onSaved?.(false)
      }
    },
    onError: (err: Error) => {
      if (err instanceof ConflictError) {
        const msg = 'تعارض نسخه: این فرم توسط کاربر دیگری تغییر کرده. صفحه را بارگذاری مجدد کنید.'
        setSaveError(msg)
        toast({ title: 'تعارض نسخه', description: msg, variant: 'destructive' })
        return
      }
      if (err instanceof RecordLockedError) {
        setSaveError(err.message)
        toast({ title: 'سابقه قفل است', description: err.message, variant: 'destructive' })
        return
      }
      if (err instanceof FormLockedError) {
        setSaveError(err.message)
        toast({ title: 'فرم قفل است', description: err.message, variant: 'destructive' })
        return
      }
      setSaveError(err.message)
      toast({ title: 'خطا در ذخیره‌سازی', description: err.message, variant: 'destructive' })
    },
  })

  saveMutationRef.current = saveMutation

  const handleSave = () => {
    if (!data || saveMutation.isPending) return
    saveMutation.mutate({ submit: false, submittingValues: fieldValues })
  }

  const handleSubmit = () => {
    if (!data) return
    setSubmitAttempted(true)
    const snapshot = runFormEngine(
      data.definition.fields,
      data.definition.conditional_rules ?? [],
      latestFieldValuesRef.current,
    )
    if (Object.keys(snapshot.blockingErrors).length > 0) return

    const stillMissing = missingJustifications(snapshot.softWarnings, justifications)
    if (stillMissing.length > 0) {
      toast({
        title: 'توجیه بالینی لازم است',
        description: `${stillMissing.length.toLocaleString('fa-IR')} هشدار بدون توجیه`,
        variant: 'destructive',
      })
      return
    }
    saveMutation.mutate({ submit: true, submittingValues: fieldValues })
  }

  // ─── Loading / Error states ──────────────────────────────────────────────────

  if (isLoading || instanceIndex === null) {
    return (
      <div className="p-8 space-y-4">
        <div className="h-8 bg-muted rounded animate-pulse w-64" />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
        <AlertCircle className="w-10 h-10 text-destructive" />
        <p className="text-sm text-muted-foreground text-center">{(error as Error).message}</p>
      </div>
    )
  }

  if (!data) return null

  const submissionStatus = data.submission?.status as string | undefined
  // needs_revision + pinned-old → user MUST upgrade before re-editing, else they
  // could resubmit against stale validation rules. Until then, lock the form.
  const forceUpgrade = data.recordStatus === 'needs_revision' && data.isPinnedOldVersion
  const blockedByForcedUpgrade = forceUpgrade && versionUpgradeChoice !== 'upgrade'
  const isReadOnly =
    !canEdit ||
    (submissionStatus === 'submitted' && data.recordStatus !== 'needs_revision') ||
    blockedByForcedUpgrade
  const showVersionSidebar = data.isPinnedOldVersion && (versionSidebarOpen || blockedByForcedUpgrade)
  const effectiveInstanceIndex = (data.submission?.instance_index as number | undefined) ?? instanceIndex

  const openDeclineFieldComments = fieldComments.filter(
    (c) => c.origin === 'decline' && !c.is_resolved,
  )
  const declineFieldKeys = Array.from(
    new Set(openDeclineFieldComments.map((c) => c.field_key).filter((k): k is string => !!k)),
  )
  const revisionFieldKeys = (() => {
    const raw = data.submission?.revision_field_keys
    return Array.isArray(raw) ? (raw as string[]) : []
  })()
  const revisionReason = data.submission?.revision_reason as string | undefined

  const formBody = (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-2xl mx-auto">
        <FormRenderer
          definition={data.definition}
          values={fieldValues}
          onChange={setField}
          mode="data-entry"
          readOnly={isReadOnly}
          submitAttempted={submitAttempted}
          resetSignal={`${formId}:${recordId}:${submissionIdProp ?? 'new'}`}
          justifications={justifications}
          onJustificationChange={(jKey, val) =>
            setJustifications((prev) => ({ ...prev, [jKey]: val }))
          }
          uploadCtx={{ projectId, recordId, formId, submissionId }}
          revisionFieldKeys={revisionFieldKeys}
          declineCommentFieldKeys={declineFieldKeys}
          renderFieldComments={(fieldKey) =>
            submissionId ? (
              <FieldCommentThread
                recordId={recordId}
                submissionId={submissionId}
                fieldKey={fieldKey}
                allComments={fieldComments}
                onResolve={() =>
                  qc.invalidateQueries({ queryKey: ['field-comments', recordId, submissionId] })
                }
              />
            ) : null
          }
          renderJustificationHistory={(fieldKey, ruleId) => {
            // M2 — surface prior justifications under each warned field. We
            // pull from the *currently persisted* flags (data.submission)
            // rather than the live in-memory map so the history reflects
            // what's in the DB, not unsaved edits.
            const flags = (data.submission?.soft_warning_flags ?? []) as SoftWarningFlag[]
            const flag = Array.isArray(flags)
              ? flags.find((f) => f.field_key === fieldKey && f.rule_id === ruleId)
              : undefined
            return <JustificationHistory flag={flag} />
          }}
        />
      </div>
    </div>
  )

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border px-6 py-3 shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-foreground">{data.formTitle}</h2>
            {data.form.instance_type === 'repeatable' && (
              <span className="text-xs text-muted-foreground">
                نمونه {effectiveInstanceIndex.toLocaleString('fa-IR')}
              </span>
            )}
            {submissionStatus === 'submitted' && (
              <span className="flex items-center gap-1 text-xs text-emerald-400">
                <CheckCircle2 className="w-3.5 h-3.5" />
                ارسال شده
              </span>
            )}
            {submissionStatus === 'draft' && (
              <span className="text-xs text-yellow-400">پیش‌نویس</span>
            )}
          </div>
          {!isReadOnly && (
            <div className="flex items-center gap-2">
              <SaveStatus
                isDirty={isDirty}
                isSaving={saveMutation.isPending}
                lastSavedAt={lastSavedAt}
              />
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                <Save className="w-4 h-4" />
                ذخیره پیش‌نویس
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                onClick={handleSubmit}
                disabled={saveMutation.isPending}
              >
                <Send className="w-4 h-4" />
                {/* I2 — state-aware: "ارسال مجدد" when re-editing after a decline.
                    `aria-live="polite"` so screen readers announce the label flip
                    when the record transitions to / out of needs_revision. */}
                <span aria-live="polite">
                  {submissionStatus === 'submitted' && data.recordStatus === 'needs_revision'
                    ? 'ارسال مجدد'
                    : 'ارسال فرم'}
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>

      <FormFillBanners
        saveError={saveError}
        openDeclineFieldCommentCount={openDeclineFieldComments.length}
        isNeedsRevisionSubmitted={
          submissionStatus === 'submitted' && data.recordStatus === 'needs_revision'
        }
        revisionReason={revisionReason}
        isPinnedOldVersionContinued={
          data.isPinnedOldVersion && versionUpgradeChoice === 'continue'
        }
        pinnedVersionLabel={data.pinnedVersionLabel}
        onUpgradeRequest={() => setVersionSidebarOpen(true)}
        isReadOnly={isReadOnly}
        isSubmitted={submissionStatus === 'submitted'}
        canEdit={canEdit}
        missingJustificationsCount={missingJustList.length}
      />

      {/* Form body — split with the version-upgrade sidebar when an old version
          is pinned, so old values stay visible while filling the new version. */}
      {showVersionSidebar ? (
        <div className="flex-1 overflow-hidden">
          <ResizableSplit
            storageKey="form-fill-version-diff-split"
            defaultLeftPct={68}
            minPct={22}
            left={formBody}
            right={
              <VersionUpgradeSidebar
                pinnedVersionLabel={data.pinnedVersionLabel}
                submissionId={data.submission?.id as string | undefined}
                submissionVersion={(data.submission?.version as number | undefined) ?? submissionVersion}
                recordId={recordId}
                formId={formId}
                upgraded={versionUpgradeChoice === 'upgrade'}
                forceUpgrade={forceUpgrade}
                onContinueOld={() => {
                  setVersionUpgradeChoice('continue')
                  setVersionSidebarOpen(false)
                }}
                onUpgraded={() => setVersionUpgradeChoice('upgrade')}
                onClose={() => setVersionSidebarOpen(false)}
              />
            }
          />
        </div>
      ) : (
        formBody
      )}
    </div>
  )
}

// ─── SaveStatus indicator (I1) ───────────────────────────────────────────────

function SaveStatus({
  isDirty, isSaving, lastSavedAt,
}: { isDirty: boolean; isSaving: boolean; lastSavedAt: number | null }) {
  if (isSaving) return <span className="text-xs text-muted-foreground">در حال ذخیره...</span>
  if (isDirty) return <span className="text-xs text-yellow-400">تغییرات ذخیره نشده</span>
  if (lastSavedAt === null) return null

  const secs = Math.max(1, Math.floor((Date.now() - lastSavedAt) / 1000))
  const label =
    secs < 60 ? `${secs.toLocaleString('fa-IR')} ثانیه پیش`
    : secs < 3600 ? `${Math.floor(secs / 60).toLocaleString('fa-IR')} دقیقه پیش`
    : `${Math.floor(secs / 3600).toLocaleString('fa-IR')} ساعت پیش`
  return <span className="text-xs text-emerald-400">ذخیره شد · {label}</span>
}
